<template>
  <div class="reflection-page">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h3 class="fw-bold text-primary">💭 Daily Reflection</h3>
        <p class="text-muted mb-0">Take a moment to reflect on your thoughts and feelings</p>
      </div>
      <button class="btn btn-outline-secondary" @click="$router.go(-1)">
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
    </div>

    <!-- Reflection Prompts -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">Today's Reflection Prompts</h5>
          <div class="row g-3">
            <div 
              v-for="prompt in reflectionPrompts" 
              :key="prompt.id"
              class="col-md-6"
            >
              <div 
                class="prompt-card p-3 cursor-pointer"
                :class="{ active: selectedPrompt?.id === prompt.id }"
                @click="selectPrompt(prompt)"
              >
                <div class="prompt-icon mb-2">{{ prompt.icon }}</div>
                <h6 class="fw-bold mb-2">{{ prompt.title }}</h6>
                <p class="text-muted small">{{ prompt.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Reflection -->
    <div v-if="selectedPrompt" class="row">
      <div class="col-lg-8">
        <div class="modern-card p-4">
          <div class="reflection-header mb-4">
            <h5 class="fw-bold">{{ selectedPrompt.title }}</h5>
            <p class="text-muted">{{ selectedPrompt.question }}</p>
          </div>

          <!-- Writing Area -->
          <div class="writing-section mb-4">
            <label class="form-label fw-bold">Your Thoughts:</label>
            <textarea 
              class="form-control reflection-textarea"
              rows="8"
              v-model="reflectionText"
              :placeholder="selectedPrompt.placeholder"
            ></textarea>
            <div class="text-muted small mt-2">
              {{ reflectionText.length }} / 500 characters
            </div>
          </div>

          <!-- Mood Selector -->
          <div class="mood-section mb-4">
            <label class="form-label fw-bold">How are you feeling about this?</label>
            <div class="mood-options d-flex gap-3 mt-2">
              <div 
                v-for="mood in moods" 
                :key="mood.name"
                class="mood-option text-center cursor-pointer"
                :class="{ selected: selectedMood === mood.name }"
                @click="selectedMood = mood.name"
              >
                <div class="mood-emoji">{{ mood.emoji }}</div>
                <small class="mood-label">{{ mood.label }}</small>
              </div>
            </div>
          </div>

          <!-- Tags -->
          <div class="tags-section mb-4">
            <label class="form-label fw-bold">Add tags (optional):</label>
            <div class="tag-input-container d-flex gap-2 mb-2">
              <input 
                type="text" 
                class="form-control"
                placeholder="Add a tag..."
                v-model="newTag"
                @keypress.enter="addTag"
              >
              <button class="btn btn-outline-primary" @click="addTag">Add</button>
            </div>
            <div class="selected-tags">
              <span 
                v-for="tag in selectedTags" 
                :key="tag"
                class="badge bg-primary me-2 mb-2"
              >
                {{ tag }}
                <i class="bi bi-x ms-1 cursor-pointer" @click="removeTag(tag)"></i>
              </span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="reflection-actions">
            <button 
              class="btn btn-outline-secondary me-2"
              @click="clearReflection"
            >
              Clear
            </button>
            <button 
              class="btn btn-primary"
              @click="saveReflection"
              :disabled="!reflectionText.trim()"
            >
              <i class="bi bi-save me-2"></i>Save Reflection
            </button>
          </div>
        </div>
      </div>

      <!-- Reflection Stats & Tips -->
      <div class="col-lg-4">
        <div class="modern-card p-4 mb-4">
          <h6 class="fw-bold mb-3">Your Reflection Journey</h6>
          
          <div class="stat-item d-flex justify-content-between mb-3">
            <span>Reflections This Week:</span>
            <span class="fw-bold">{{ weeklyStats.reflections }}</span>
          </div>
          
          <div class="stat-item d-flex justify-content-between mb-3">
            <span>Current Streak:</span>
            <span class="fw-bold">{{ weeklyStats.streak }} days</span>
          </div>
          
          <div class="stat-item d-flex justify-content-between mb-3">
            <span>Total Words Written:</span>
            <span class="fw-bold">{{ weeklyStats.totalWords }}</span>
          </div>

          <div class="progress-container mb-3">
            <small class="text-muted">Weekly Goal Progress</small>
            <div class="progress mt-1">
              <div 
                class="progress-bar bg-primary" 
                :style="{ width: weeklyProgress + '%' }"
              ></div>
            </div>
            <small class="text-muted">{{ weeklyStats.days }}/7 days</small>
          </div>

          <div v-if="Object.keys(weeklyStats.moods || {}).length" class="mt-2">
            <small class="text-muted d-block mb-1">Moods this week</small>
            <div class="d-flex flex-wrap gap-2">
              <span v-for="(count, emoji) in weeklyStats.moods" :key="emoji" class="badge bg-light text-dark">
                <span class="me-1">{{ emoji }}</span>{{ count }}
              </span>
            </div>
          </div>
        </div>

        <div class="modern-card p-4">
          <h6 class="fw-bold mb-3">💡 Reflection Tips</h6>
          <div class="tips-list">
            <div 
              v-for="tip in reflectionTips" 
              :key="tip.id"
              class="tip-item mb-3"
            >
              <div class="tip-icon me-2">{{ tip.icon }}</div>
              <small>{{ tip.text }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Previous Reflections -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">Previous Reflections</h5>
          <div class="previous-reflections">
            <div 
              v-for="reflection in previousReflections" 
              :key="reflection.id"
              class="reflection-item p-3 mb-3 border rounded"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <h6 class="fw-bold mb-0">{{ reflection.prompt }}</h6>
                <small class="text-muted">{{ formatDate(reflection.date) }}</small>
              </div>
              <p class="text-muted mb-2">{{ reflection.text.substring(0, 150) }}...</p>
              <div class="d-flex align-items-center">
                <span class="mood-indicator me-2">{{ reflection.mood }}</span>
                <div class="reflection-tags">
                  <span 
                    v-for="tag in reflection.tags" 
                    :key="tag"
                    class="badge bg-light text-dark me-1"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showSuccessModal, 'd-block': showSuccessModal }"
      v-if="showSuccessModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center p-4">
            <div class="success-icon mb-3">✨</div>
            <h5 class="fw-bold mb-2">Reflection Saved!</h5>
            <p class="text-muted mb-4">Great job taking time to reflect on your thoughts and feelings.</p>
            <button 
              class="btn btn-primary"
              @click="showSuccessModal = false"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showSuccessModal"></div>
  </div>
</template>

<script>
import {
  getPrompts,
  getHistory,
  createReflection
} from '../services/reflectionService.js'

export default {
  name: 'ReflectionPage',
  data() {
    return {
      selectedPrompt: null,
      reflectionText: '',
      selectedMood: null,
      selectedTags: [],
      newTag: '',
      showSuccessModal: false,
      isLoading: false,
      loadError: '',
      
      // Will be loaded from backend; fallback populated if request fails
      reflectionPrompts: [],
      
      moods: [
        { name: 'happy', emoji: '😊', label: 'Happy' },
        { name: 'excited', emoji: '🤗', label: 'Excited' },
        { name: 'calm', emoji: '😌', label: 'Calm' },
        { name: 'thoughtful', emoji: '🤔', label: 'Thoughtful' },
        { name: 'proud', emoji: '😤', label: 'Proud' },
        { name: 'curious', emoji: '🧐', label: 'Curious' }
      ],
      
      weeklyStats: { reflections: 0, days: 0, streak: 0, totalWords: 0, avgWords: 0, moods: {} },
      
      reflectionTips: [
        { id: 1, icon: '✍️', text: 'Write honestly about your feelings' },
        { id: 2, icon: '🎯', text: 'Focus on specific moments or experiences' },
        { id: 3, icon: '🌱', text: 'Think about how you\'ve grown' },
        { id: 4, icon: '❤️', text: 'Be kind to yourself in your reflections' }
      ],
      
      previousReflections: []
    }
  },
  computed: {
    weeklyProgress() {
      return Math.round(((this.weeklyStats.days || 0) / 7) * 100)
    }
  },
  async mounted() {
    await this.fetchPromptsAndHistory()
  },
  methods: {
    currentUser() {
      const raw = localStorage.getItem('currentUser')
      return raw ? JSON.parse(raw) : null
    },

    async fetchPromptsAndHistory() {
      try {
        this.isLoading = true
        this.loadError = ''
        const user = this.currentUser()
        const userId = user?.id
        // Load prompts
        try {
          const prompts = await getPrompts(userId || 0)
          const iconMap = {
            'Gratitude Moment': '🙏',
            'Learning Reflection': '📚',
            'Challenge & Growth': '💪',
            'Future Dreams': '🌟'
          }
          this.reflectionPrompts = (Array.isArray(prompts) ? prompts : []).map((p, idx) => ({
            id: idx + 1,
            title: p.name || 'Reflection',
            description: p.details || p.detes || '',
            icon: iconMap[p.name] || '📝',
            question: p.details || p.detes || 'Write your thoughts:',
            placeholder: 'Start writing...'
          }))
        } catch (e) {
          // Fallback static prompts
          this.reflectionPrompts = [
            { id: 1, title: 'Gratitude Moment', description: 'What are you grateful for today?', icon: '🙏', question: 'Think about something or someone you\'re grateful for today. How does it make you feel?', placeholder: 'Today I am grateful for...' },
            { id: 2, title: 'Learning Reflection', description: 'What did you learn today?', icon: '📚', question: 'Reflect on something new you learned today. How can you use this knowledge?', placeholder: 'Today I learned that...' },
            { id: 3, title: 'Challenge & Growth', description: 'How did you overcome a challenge?', icon: '💪', question: 'Think about a challenge you faced today. How did you handle it and what did you learn?', placeholder: 'Today I faced a challenge when...' },
            { id: 4, title: 'Future Dreams', description: 'What are you excited about?', icon: '🌟', question: 'What are you looking forward to? What dreams or goals are you working toward?', placeholder: 'I am excited about...' }
          ]
        }

        // Auto-select first prompt
        if (this.reflectionPrompts.length > 0) {
          this.selectPrompt(this.reflectionPrompts[0])
        }

        // Load history
        if (userId) {
          try {
            const history = await getHistory(userId)
            this.previousReflections = (Array.isArray(history) ? history : []).map((r) => ({
              id: r.id,
              prompt: r.category,
              text: r.content || '',
              mood: this.moodToEmoji(r.feeling || 'happy'),
              tags: Array.isArray(r.tags) ? r.tags : (r.tags ? [].concat(r.tags) : []),
              date: r.date_written ? new Date(r.date_written) : new Date()
            }))
          } catch (e) {
            // Keep empty if backend not ready
            this.previousReflections = []
          }
          this.computeWeeklyStats()
        }
      } catch (err) {
        this.loadError = err?.message || 'Failed to load reflections.'
      } finally {
        this.isLoading = false
      }
    },

    selectPrompt(prompt) {
      this.selectedPrompt = prompt
      this.clearReflection()
    },
    
    addTag() {
      if (this.newTag.trim() && !this.selectedTags.includes(this.newTag.trim())) {
        this.selectedTags.push(this.newTag.trim())
        this.newTag = ''
      }
    },
    
    removeTag(tag) {
      this.selectedTags = this.selectedTags.filter(t => t !== tag)
    },
    
    clearReflection() {
      this.reflectionText = ''
      this.selectedMood = null
      this.selectedTags = []
      this.newTag = ''
    },
    
    async saveReflection() {
      if (!this.reflectionText.trim() || !this.selectedPrompt) return
      const user = this.currentUser()
      const userId = user?.id
      const feeling = this.selectedMood || 'happy'
      const payload = {
        category: this.selectedPrompt.title,
        tags: this.selectedTags,
        feeling: feeling,
        content: this.reflectionText
      }

      // Try backend first
      let saved = false
      if (userId) {
        try {
          await createReflection(userId, payload)
          saved = true
          await this.refreshHistory()
          this.showFeedback('Reflection saved successfully! 📝', 'success')
        } catch (e) {
          console.error('Failed to save reflection to backend:', e)
          this.showFeedback('Failed to save reflection to server. Saved locally instead.', 'warning')
          saved = false
        }
      }

      // Local fallback to keep UX responsive
      if (!saved) {
        const newReflection = {
          id: Date.now(),
          prompt: this.selectedPrompt.title,
          text: this.reflectionText,
          mood: this.moodToEmoji(feeling),
          tags: [...this.selectedTags],
          date: new Date()
        }
        this.previousReflections.unshift(newReflection)
        this.computeWeeklyStats()
        
        if (!userId) {
          this.showFeedback('Reflection saved locally. Login to sync across devices.', 'info')
        }
      }

      this.showSuccessModal = true
      this.clearReflection()
    },

    async refreshHistory() {
      const user = this.currentUser()
      if (!user?.id) return
      try {
        const history = await getHistory(user.id)
        this.previousReflections = (Array.isArray(history) ? history : []).map((r) => ({
          id: r.id,
          prompt: r.category,
          text: r.content || '',
          mood: this.moodToEmoji(r.feeling || 'happy'),
          tags: Array.isArray(r.tags) ? r.tags : (r.tags ? [].concat(r.tags) : []),
          date: r.date_written ? new Date(r.date_written) : new Date()
        }))
        this.computeWeeklyStats()
      } catch (e) {
        console.error('Failed to refresh reflection history:', e)
        this.showFeedback('Failed to load recent reflections. Please refresh the page.', 'error')
      }
    },

    moodToEmoji(mood) {
      const found = this.moods.find(m => m.name === mood)
      return found ? found.emoji : '😊'
    },

    showFeedback(message, type) {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    },

    computeWeeklyStats() {
      const now = new Date()
      const start = new Date(now)
      start.setDate(now.getDate() - 6) // last 7 days including today
      const withinWeek = this.previousReflections.filter(r => r.date >= start)
      const reflections = withinWeek.length
      const daysSet = new Set(withinWeek.map(r => new Date(r.date.getFullYear(), r.date.getMonth(), r.date.getDate()).toISOString()))
      const days = daysSet.size
      const totalWords = this.previousReflections.reduce((sum, r) => sum + (r.text ? r.text.trim().split(/\s+/).length : 0), 0)

      // Simple streak: count consecutive days with at least one reflection, back from today
      let streak = 0
      let dayCursor = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const byDay = new Set(this.previousReflections.map(r => new Date(r.date.getFullYear(), r.date.getMonth(), r.date.getDate()).toISOString()))
      while (byDay.has(dayCursor.toISOString())) {
        streak += 1
        dayCursor.setDate(dayCursor.getDate() - 1)
      }
      // Mood distribution for this week (by emoji)
      const moodCounts = withinWeek.reduce((acc, r) => {
        const emoji = r.mood || '😊'
        acc[emoji] = (acc[emoji] || 0) + 1
        return acc
      }, {})

      const avgWords = reflections > 0 ? Math.round(totalWords / reflections) : 0
      this.weeklyStats = { reflections, days, totalWords, avgWords, streak, moods: moodCounts }
    },
    
    formatDate(date) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.prompt-card {
  border: 2px solid transparent;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: var(--bg-primary);
}

.prompt-card:hover {
  border-color: var(--role-primary);
  transform: translateY(-2px);
}

.prompt-card.active {
  border-color: var(--role-primary);
  background: var(--role-primary);
  color: white;
}

.prompt-icon {
  font-size: 2rem;
}

.reflection-textarea {
  min-height: 200px;
  resize: vertical;
}

.mood-option {
  padding: 0.5rem;
  border: 2px solid transparent;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.mood-option:hover {
  border-color: var(--role-primary);
}

.mood-option.selected {
  border-color: var(--role-primary);
  background: var(--bg-secondary);
}

.mood-emoji {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.mood-label {
  display: block;
  font-weight: 500;
}

.tip-item {
  display: flex;
  align-items: flex-start;
}

.tip-icon {
  font-size: 1rem;
  margin-top: 0.1rem;
}

.reflection-item {
  background: var(--bg-secondary);
  transition: all 0.2s ease;
}

.reflection-item:hover {
  background: var(--bg-primary);
}

.mood-indicator {
  font-size: 1.2rem;
}

.success-icon {
  font-size: 3rem;
}

.cursor-pointer {
  cursor: pointer;
}

.modal.show {
  background: rgba(0, 0, 0, 0.5);
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
</style> 